import React from 'react';
import { useReportVisibility } from '../contexts/ReportVisibilityContext';
import { Button } from './ui/button';
import { EyeOff, Save, RotateCcw, AlertTriangle } from 'lucide-react';

/**
 * Human-readable labels for visibility keys
 */
const VISIBILITY_KEY_LABELS = {
  // Decision Context Panel (Precision Fish Detection)
  'dcp.header': 'Decision Class Header',
  'dcp.detection': 'Detection Box',
  'dcp.data_reliability': 'Data Reliability Box',
  'dcp.financial_context': 'Financial Context Box',
  'dcp.actions': 'Actions Box',
  'dcp.stock_record_policy': 'Stock Record Policy',
  'dcp.validation_banner': 'Validation Required Banner',
  'dcp.audit_footer': 'Audit Footer',
  // Growth Intelligence Summary
  'gis.growth_direction': 'Growth Direction',
  'gis.detection_strength': 'Detection Strength',
  'gis.data_quality': 'Data Quality',
  'gis.trend_classification': 'Trend Classification',
  'gis.deviation_assessment': 'Deviation Assessment',
  'gis.feeding_impact': 'Feeding Impact',
  'gis.adjusted_fish_count': 'Adjusted Fish Count',
  'gis.estimated_missing_fish': 'Estimated Missing Fish',
  'gis.suspect_sample': 'Suspect Sample',
  // Detection Reconciliation
  'recon.detection_strength_badge': 'Detection Strength Badge',
  'recon.agreement_status': 'Agreement Status',
  'recon.fcr_based': 'FCR-Based Detection',
  'recon.jump_based': 'Jump-Based Detection',
  'recon.growth_fit': 'Growth Fit',
  // Data Quality Indicator
  'dq.overall_score': 'Overall Score',
  'dq.warning': 'Data Quality Warning',
  'dq.sample_frequency': 'Sample Frequency',
  'dq.feed_continuity': 'Feed Continuity',
  'dq.mortality_reporting': 'Mortality Reporting',
  'dq.temperature_data': 'Temperature Data',
  // Sampling Recommendation
  'sampling.recommendation': 'Sampling Recommendation',
  'sampling.confidence': 'Sampling Confidence',
  // Feeding Impact
  'feeding.state': 'Feeding State',
  'feeding.rfi': 'RFI Value',
  'feeding.note': 'Feeding Note',
  'feeding.recent_alert': 'Recent RFI Alert',
  // Growth Fit
  'growthfit.best_fit_count': 'Best Fit Count',
  'growthfit.estimated_missing': 'Estimated Missing',
  'growthfit.fit_quality': 'Fit Quality',
  'growthfit.explanation': 'Explanation',
  // Economic Impact
  'econ.avoidable_feed_cost': 'Avoidable Feed Cost',
  'econ.revenue_risk': 'Revenue Risk',
  'econ.total_impact': 'Total Impact',
  'econ.delayed_detection_cost': 'Delayed Detection Cost',
  'econ.financial_context': 'Financial Context',
  // Advanced Signals
  'adv.growth_pattern': 'Growth Pattern',
  'adv.signal_strength': 'Signal Strength',
  'adv.biomass_density': 'Biomass Density',
  'adv.incorrect_samples': 'Incorrect Samples',
  'adv.historical_deviation': 'Historical Deviation',
  'adv.data_quality_warnings': 'Data Quality Warnings',
  'adv.hist_deviation_first': 'First Deviation',
  'adv.hist_strongest_window': 'Strongest Window',
  'adv.hist_persistence': 'Persistence',
  'adv.hist_volatility': 'Volatility',
  'adv.hist_analytical_strength': 'Analytical Strength',
  // MFI Report sections
  'mfi.growth_intelligence_summary': 'Growth Intelligence Summary',
  'mfi.decision_context': 'Decision Context',
  'mfi.analysis_summary': 'Analysis Summary',
  'mfi.growth_fit_analysis': 'Growth Fit Analysis',
  'mfi.data_quality_indicator': 'Data Quality Indicator',
  'mfi.feeding_impact_classification': 'Feeding Impact Classification',
  'mfi.economic_impact': 'Economic Impact',
  'mfi.detection_reconciliation': 'Detection Reconciliation',
  'mfi.sampling_recommendation': 'Sampling Recommendation',
  'mfi.advanced_signals': 'Advanced Signals',
  'mfi.trajectory_jump_alert': 'Trajectory Jump Alert',
  'mfi.data_quality_warnings': 'Data Quality Warnings',
  'mfi.conflict_notice': 'Conflict Notice',
  'mfi.mortality_check': 'Mortality Check',
  'mfi.deviation_table': 'Deviation Table',
  'mfi.confidence_key': 'Confidence & Data Quality Key',
  'mfi.cannibalism_risk': 'Cannibalism Risk Indicator',
  'mfi.method_conflict': 'Method Conflict Warning',
  // Page-level sections
  'page.key_insight': 'Key Insight',
  'page.top_level_signal': 'Top Level Signal',
  'page.feed_variance_comparison': 'Feed Variance Comparison',
  'page.stock_integrity_score': 'Stock Integrity Score',
  'page.fish_count_adjustment': 'Fish Count Adjustment',
  // Snapshot sections
  'snapshot.interpretation': 'Interpretation',
  'snapshot.warning_note': 'Warning Note',
  // Banner
  'banner.title': 'Banner Title',
  'banner.body': 'Banner Body Text',
  'banner.verification_warning': 'Verification Warning',
};

/**
 * Get human-readable label for a visibility key
 */
const getLabel = (key) => VISIBILITY_KEY_LABELS[key] || key;

/**
 * Group keys by category
 */
const groupByCategory = (keys) => {
  const groups = {
    'Decision Context Panel': [],
    'Growth Intelligence Summary': [],
    'Detection Reconciliation': [],
    'Data Quality': [],
    'Sampling': [],
    'Feeding': [],
    'Growth Fit': [],
    'Economic Impact': [],
    'Advanced Signals': [],
    'MFI Report Sections': [],
    'Page Sections': [],
    'Banner': [],
    'Other': [],
  };

  keys.forEach(key => {
    if (key.startsWith('dcp.')) groups['Decision Context Panel'].push(key);
    else if (key.startsWith('gis.')) groups['Growth Intelligence Summary'].push(key);
    else if (key.startsWith('recon.')) groups['Detection Reconciliation'].push(key);
    else if (key.startsWith('dq.')) groups['Data Quality'].push(key);
    else if (key.startsWith('sampling.')) groups['Sampling'].push(key);
    else if (key.startsWith('feeding.')) groups['Feeding'].push(key);
    else if (key.startsWith('growthfit.')) groups['Growth Fit'].push(key);
    else if (key.startsWith('econ.')) groups['Economic Impact'].push(key);
    else if (key.startsWith('adv.')) groups['Advanced Signals'].push(key);
    else if (key.startsWith('mfi.')) groups['MFI Report Sections'].push(key);
    else if (key.startsWith('page.') || key.startsWith('snapshot.')) groups['Page Sections'].push(key);
    else if (key.startsWith('banner.')) groups['Banner'].push(key);
    else groups['Other'].push(key);
  });

  // Remove empty groups
  return Object.fromEntries(
    Object.entries(groups).filter(([_, keys]) => keys.length > 0)
  );
};

/**
 * AdminHiddenSectionsOverview — Shows admins which sections are currently hidden
 */
const AdminHiddenSectionsOverview = ({ className = '' }) => {
  const { isAdmin, hiddenSectionsList, hasUnsavedChanges, saveVisibility, discardChanges } = useReportVisibility();

  if (!isAdmin) return null;

  const groupedHidden = groupByCategory(hiddenSectionsList);
  const hasHiddenSections = hiddenSectionsList.length > 0;

  return (
    <div className={`bg-white border border-purple-200 rounded-lg p-4 ${className}`} data-testid="admin-hidden-sections-overview">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
          <EyeOff className="w-4 h-4" />
          Hidden Report Sections
        </h4>
        {hasUnsavedChanges && (
          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full animate-pulse">
            Unsaved changes
          </span>
        )}
      </div>

      {!hasHiddenSections ? (
        <p className="text-sm text-gray-500 italic">
          All report sections are currently visible to users.
        </p>
      ) : (
        <div className="space-y-3">
          {Object.entries(groupedHidden).map(([category, keys]) => (
            <div key={category}>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                {category}
              </p>
              <div className="flex flex-wrap gap-1">
                {keys.map(key => (
                  <span
                    key={key}
                    className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded border border-red-200"
                    data-testid={`hidden-key-${key}`}
                  >
                    {getLabel(key)}
                  </span>
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-200">
            Total: {hiddenSectionsList.length} section{hiddenSectionsList.length !== 1 ? 's' : ''} hidden from users
          </p>
        </div>
      )}

      {/* Save/Discard Buttons */}
      {hasUnsavedChanges && (
        <div className="mt-4 pt-3 border-t border-purple-200 flex items-center gap-2">
          <Button
            onClick={saveVisibility}
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="save-visibility-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1" />
            Save Changes
          </Button>
          <Button
            onClick={discardChanges}
            variant="outline"
            size="sm"
            className="border-gray-300 text-gray-600"
            data-testid="discard-visibility-btn"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Discard
          </Button>
          <AlertTriangle className="w-4 h-4 text-amber-500 ml-auto" />
          <span className="text-xs text-amber-600">Changes not yet saved</span>
        </div>
      )}
    </div>
  );
};

export default AdminHiddenSectionsOverview;
